import { legacyLocalLabel } from '../../../../utilities/legacyDatePresentation';
import { useCallback } from "react";
import React, { useEffect, useState } from "react";
import UsageSummary from "./UsageSummary";
import BillingCalculation from "./BillingCalculation";
import PaymentMethods from "./PaymentMethods";
import BillingHistory from "./BillingHistory";
import { SubscriptionDto } from "../../../../interfaces";
import { useAbortManager } from "../../../../hooks/cancelletionManagerHook/useAbortManager";
import { useAuth } from "../../../../firebase/AuthProvider";
import { getMyOrganizationSubscriptionAsync } from "../../../../services/PaymentServices/SubscriptionService";
import { ROLES, SubscriptionStatus } from "../../../../Data/SD";
import { Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../../Data/Routes/Routes";
import CreditBalanceToggle from "./CreditBalanceToggle ";
import { hasPolicyPermissionV2 } from "../../../../utilities/common/policyFuntions";
import { POLICYPERMISSIONS } from "../../../../Data/PolicyPermissions";
import { SubscriptionStatusLabel } from "../../../../utilities/common/commonFuntion";
import "./BillingDetails.css";
import { toast } from "react-toastify";

import { getDisplayErrorMessage } from "../../../../utilities/ErrorMessages/getErrorMessage";

const BillingDetails: React.FC = () => {
    const { currentUser, currentRoles, currentOrganizationRoles, userAddedInRolePolicy, currentOrganizationRolePolicies } = useAuth();
    // for cancellation of api
    const cancelletionManager = useAbortManager();
    const navigate = useNavigate();
    const [subscription, setSubscription] = useState<SubscriptionDto>();
    const [loading, setLoading] = useState({
        subscription: false
    })

    const { orgId } = useParams<{ orgId: string }>();

    const isSuperAdmin = currentRoles?.some((role) => role.name === ROLES.SUPER_ADMIN);

    const isOwner = currentRoles?.some((role) => role.name === ROLES.OWNER) || currentOrganizationRoles?.some((role) => role.systemKey === "ORG_OWNER");
    const hasAccessOfBilling = hasPolicyPermissionV2(
        currentOrganizationRolePolicies,
        POLICYPERMISSIONS.BillingPolicies.View.key,
        userAddedInRolePolicy
    );

    const getSubscriptionOfOrg = useCallback(async (showLoading: boolean = true) => {
        if (!currentUser) return;

        try {
            const token = await currentUser.getIdToken();
            const controller = cancelletionManager.create("getSubscription");
            if (showLoading) setLoading(prev => ({ ...prev, subscription: true }));
            var subscriptionRes = await getMyOrganizationSubscriptionAsync(token, true, Number(orgId), controller.signal);

            if (subscriptionRes.success) {
                setSubscription(subscriptionRes.data);
            }
        } catch (error: any) {
            if (error.name === 'AbortError') return;

            const statusCode = error.response?.status || error.response?.data?.statusCode;
            if (statusCode === 404) {
                setSubscription(undefined);
            } else if (statusCode === 401 || statusCode === 403) {
                navigate(ROUTES.Forbidden());
            }
            toast.error(getDisplayErrorMessage(error, "Error to get the data"));
        } finally {
            if (showLoading) setLoading(prev => ({ ...prev, subscription: false }));
            cancelletionManager.remove("getSubscription");
        }
    }, [currentUser, orgId, navigate, cancelletionManager])

useEffect(() => {
        getSubscriptionOfOrg();

        return () => {
            cancelletionManager.abortAll();  // cancel all the requests
        };
    }, [getSubscriptionOfOrg, cancelletionManager]);



    const refreshSubscription = async () => {
        await getSubscriptionOfOrg(false);
    };

    const navigateToPricingPage = () => {
        navigate(ROUTES.PRICIMG());
    };

    if (!subscription) return (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "60vh", gap: "10px" }}
        >
            {loading.subscription ? (
                <div><Spinner size="sm" /> Loading...</div>
            ) : (
                <>
                        <div>You don't have an active subscription plan.</div>
                    <button
                        className="btn btn-primary"
                        onClick={navigateToPricingPage}
                        disabled={isSuperAdmin}
                    >
                        Start Subscription
                    </button>
                </>
            )}
        </div>
    )

    if (!isOwner && !hasAccessOfBilling) {
        return <div>You don't have the permission</div>;
    }

    return (
        <>
            <div className="billing-container">
                <div className="billing-header">
                    <div>
                        <h2>Pay As You Go Billing</h2>
                        <p>Only pay for what you use. No monthly commitments.</p>
                    </div>

                    <div className="billing-badges">
                        <span className="plan-badge">
                            {
                                subscription?.status === SubscriptionStatus.ACTIVE.toString() && subscription.plan
                                    ? subscription.plan.name
                                    : "No Plan"
                            }
                        </span>
                        {/* <span className="date-badge">Feb 1 – Feb 29, 2026</span> */}
                        <span className="date-badge">
                            {
                                subscription?.status === SubscriptionStatus.ACTIVE.toString()
                                    ? <>{legacyLocalLabel(subscription.currentPeriodStart, "MMM d, yyyy")} - {legacyLocalLabel(subscription.currentPeriodEnd, "MMM d, yyyy")}</>
                                    : <>{legacyLocalLabel(subscription?.trialStart, "MMM d, yyyy")} - {legacyLocalLabel(subscription?.trialEnd, "MMM d, yyyy")}</>
                            }
                        </span>
                        <span
                            className={
                                subscription?.status === SubscriptionStatus.ACTIVE.toString() ||
                                    subscription?.status === SubscriptionStatus.TRIAL_ACTIVE.toString()
                                    ? "green-badge"
                                    : "red-badge"
                            }
                        >
                            {SubscriptionStatusLabel[subscription?.status as unknown as SubscriptionStatus]}
                        </span>
                        {
                            subscription?.status !== SubscriptionStatus.ACTIVE.toString() && (<span className="plan-badge">
                                <button onClick={navigateToPricingPage} disabled={isSuperAdmin}>Start Subscription</button>
                            </span>)
                        }

                    </div>
                </div>

                <UsageSummary subscription={subscription} />

                {
                    !isSuperAdmin && (
                        <CreditBalanceToggle subscripton={subscription} setSubscription={setSubscription} />
                    )
                }

                <div className="bl_grid_container">
                    <BillingCalculation subscription={subscription} isSuperAdmin={isSuperAdmin} orgId={Number(orgId)} refreshSubscription={refreshSubscription} />
                    <PaymentMethods subscription={subscription} isSuperAdmin={isSuperAdmin} orgId={Number(orgId)} />
                </div>

                <BillingHistory orgId={Number(orgId)} isSuperAdmin={isSuperAdmin} />
            </div>
        </>
    );
};

export default BillingDetails;